/* eslint-disable no-console */
import { SeverityLevel } from '@microsoft/applicationinsights-common';

import { severityLevelToLogLevel } from '../../constants/constants';
import {
    ILoggerContext,
    LogType,
} from '../../models/logger';
import { removeObjectUndefinedProperties } from '../helper';
import {
    getAccountName,
    getSpaceName,
    getTenantName,
} from '../urlHelper';
import { initializeAppInsights } from './appInsights';

export default class Logger {
    // #region Static Fields
    static isDebugMode: boolean = localStorage.getItem('SHELL_DEBUG') === 'true';

    static appInsights = initializeAppInsights();
    static appInsightsMinimumSeverityLevel = SeverityLevel.Information;
    // #endregion

    // #region Public Methods
    /**
     * Toggles debug mode for the logger so everything will be printed to console
     * @param debugMode the debug mode to set
     */
    public static setDebugMode(debugMode: boolean) {
        this.isDebugMode = debugMode;
    }

    /**
     * Sets the AppInsights log level to prevent logging too many traces for apollo
     * We are using the same severity level as AppInsights (0 = debug/verbose, 1 = info, 2 = warn, 3 = error, 4 = critical)
     * @param level the log level to set
     */
    public static setAppInsightsMinimumSeverityLevel(severityLevel: SeverityLevel | number) {
        this.appInsightsMinimumSeverityLevel = severityLevel;
    }

    /**
     * Logs debug messages to the console if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the debug message (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the debug message
     * @param context the context of the debug message (e.g. store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static debug(scope: string | string[], message: string, context?: ILoggerContext) {
        this.log(scope, message, SeverityLevel.Verbose, context);
    }

    /**
     * Logs info messages to the console if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the debug info (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the debug info
     * @param context the context of the debug info (e.g. store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static info(scope: string | string[], message: string, context?: ILoggerContext) {
        this.log(scope, message, SeverityLevel.Information, context);
    }

    /**
     * Logs warning messages to the console if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the warning message (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the warning message
     * @param context the context of the warning message (eg store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static warn(scope: string | string[], message: string, context?: ILoggerContext) {
        this.log(scope, message, SeverityLevel.Warning, context);
    }

    /**
     * Logs error messages to the console if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the error message (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the error message
     * @param context the context of the error message (e.g. store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static error(scope: string | string[], message: string, context?: ILoggerContext) {
        this.log(scope, message, SeverityLevel.Error, context);
    }

    /**
     * Logs critical messages to the console if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the critical message (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the critical message
     * @param context the context of critical message (e.g. store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static critical(scope: string | string[], message: string, context?: ILoggerContext) {
        this.log(scope, message, SeverityLevel.Critical, context);
    }

    /**
     * Logs messages to the console based on the severity level if debug mode is enabled
     * Being as specific as possible helps a lot with debugging
     * @param scope the scope of the message (e.g. 'portal-nav', ['portal-nav', 'componentWillLoad'])
     * @param message the message of the message
     * @param severityLevel the severity level of the message
     * @param context the context of the message (e.g. store state, object, includeStack, forcePrintToConsole, forcePushToAppInsights)
     */
    public static log(scope: string | string[], message: string, severityLevel: SeverityLevel, context?: ILoggerContext) {
        const contextWithStack = this.addStackToContext(context);
        this.printToConsole(severityLevelToLogLevel[severityLevel], scope, message, contextWithStack);

        if (contextWithStack?.forcePushToAppInsights || this.appInsightsMinimumSeverityLevel <= severityLevel) {
            this.recordToAppInsights(scope, message, severityLevel, contextWithStack);
        }
    }
    // #endregion

    // #region Private Methods
    private static isDebugEnabled() {
        return this.isDebugMode;
    }

    private static getFormattedMessage(scope: string | string[], message: string) {
        return `[${Array.isArray(scope) ? scope.join('/') : scope}] - ${message}`;
    }

    private static addStackToContext(context?: ILoggerContext) {
        if (context?.includeStack && !context.stack) {
            return {
                ...context,
                stack: (new Error()).stack,
            };
        }
        return context;
    }

    private static printToConsole(
        logLevel: LogType,
        scope: string | string[], message: string,
        context?: ILoggerContext,
    ) {
        const {
            object, stack, forcePrintToConsole,
        } = context ?? {};

        if (forcePrintToConsole || this.isDebugEnabled()) {
            console[logLevel](`[ap-shell]${this.getFormattedMessage(scope, message)}`);
            if (object) {
                console[logLevel](JSON.stringify(object, undefined, 4));
            }
            if (stack) {
                console[logLevel](stack);
            }
        }
    }

    private static recordToAppInsights(
        scope: string | string[],
        message: string,
        severityLevel: SeverityLevel | number,
        context?: ILoggerContext,
    ) {
        const {
            state, object, stack,
        } = context ?? {};

        let customDimensions = {
            component: JSON.stringify(scope),
            scope: JSON.stringify(scope),
            object: JSON.stringify(object),
            stack,
            accountName: state?.accountName ?? getAccountName(),
            tenantName: state?.currentTenant ?? getTenantName(),
            orgId: state?.organizationId,
            space: state?.active ?? getSpaceName(),
            locale: state?.locale,
            theme: state?.theme,
        };

        customDimensions = removeObjectUndefinedProperties(customDimensions);

        this.appInsights?.trackTrace({
            message: this.getFormattedMessage(scope, message),
            severityLevel,
        }, customDimensions);
    }
    // #endregion
}
